import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // SEO Meta Tags
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

    const schemaOrg = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "@id": `${siteUrl}/#organization`,
          "name": "Mentoria IMPACTO",
          "url": siteUrl,
          "logo": {
            "@type": "ImageObject",
            "url": "https://static.readdy.ai/image/5759e80cb249fc12074e871219d36a56/45ad6a47b9fb48ee035b554bf9c9a7f3.png",
            "width": 250,
            "height": 60
          },
          "description": "Mentoria de Governo Interno Feminino para mulheres empreendedoras",
          "sameAs": [
            "https://www.instagram.com/larimoraesreal/"
          ]
        },
        {
          "@type": "WebSite",
          "@id": `${siteUrl}/#website`,
          "url": siteUrl,
          "name": "Mentoria IMPACTO",
          "description": "O Código do Governo Interno para mulheres que decidiram se governar",
          "publisher": {
            "@id": `${siteUrl}/#organization`
          },
          "inLanguage": "pt-BR"
        },
        {
          "@type": "WebPage",
          "@id": `${siteUrl}/#webpage`,
          "url": siteUrl,
          "name": "Mentoria IMPACTO - Governo Interno Feminino para Mulheres Empreendedoras",
          "isPartOf": {
            "@id": `${siteUrl}/#website`
          },
          "about": {
            "@id": `${siteUrl}/#organization`
          },
          "description": "Mentoria IMPACTO: O Código do Governo Interno para mulheres empreendedoras que cansaram de promessas vazias. Aprenda a se governar, tomar decisões conscientes e manifestar com responsabilidade.",
          "inLanguage": "pt-BR"
        },
        {
          "@type": "Service",
          "@id": `${siteUrl}/#service`,
          "serviceType": "Mentoria de Desenvolvimento Pessoal e Profissional",
          "name": "Mentoria IMPACTO - Governo Interno Feminino",
          "description": "Processo profundo e prático que integra identidade real, planejamento pessoal, blindagem da mentalidade, mudança de comportamento, decisão consciente, execução possível e manifestação com responsabilidade.",
          "provider": {
            "@id": `${siteUrl}/#organization`
          },
          "areaServed": {
            "@type": "Country",
            "name": "Brasil"
          },
          "audience": {
            "@type": "PeopleAudience",
            "audienceType": "Mulheres empreendedoras, empresárias e líderes"
          },
          "offers": {
            "@type": "Offer",
            "availability": "https://schema.org/InStock",
            "url": "https://forms.gle/M5HGeQ7kXMsa2ekJ9"
          }
        },
        {
          "@type": "Person",
          "@id": `${siteUrl}/#person`,
          "name": "Larissa Moraes",
          "jobTitle": "Mentora de Governo Interno Feminino",
          "description": "Há mais de 20 anos transitando entre estratégia, comportamento humano e vida real. Especialista em ajudar mulheres a se governarem.",
          "image": "https://static.readdy.ai/image/5759e80cb249fc12074e871219d36a56/af7228439990b621ad207692bf50020e.jpeg",
          "sameAs": [
            "https://www.instagram.com/larimoraesreal/"
          ],
          "worksFor": {
            "@id": `${siteUrl}/#organization`
          }
        },
        {
          "@type": "FAQPage",
          "@id": `${siteUrl}/#faq`,
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Para quem é a Mentoria IMPACTO?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "A Mentoria IMPACTO é para mulheres empreendedoras, empresárias ou líderes; mulheres inteligentes, sensíveis e profundas; mulheres cansadas de girar em ciclos; mulheres que querem prosperar sem se perder; e mulheres prontas para assumir responsabilidade pela própria vida."
              }
            },
            {
              "@type": "Question",
              "name": "O que é o Governo Interno?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Governo Interno é a capacidade de se conduzir com autonomia, tomar decisões conscientes sem depender de validação externa, e sustentar aquilo que se deseja viver. É sobre não terceirizar suas decisões para o marketing, o medo, o sistema ou especialistas externos."
              }
            },
            {
              "@type": "Question",
              "name": "O que você conquista ao passar pela IMPACTO?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Você conquista clareza profunda sobre quem você é, organização intencional dos próximos anos da sua vida, fim das ilusões internas, mais decisão e menos oscilação emocional, execução possível com o que você já tem, posicionamento firme e respeitado, autonomia emocional, financeira e comportamental, e solidez interna com direção clara."
              }
            },
            {
              "@type": "Question",
              "name": "Para quem NÃO é a Mentoria IMPACTO?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "A Mentoria IMPACTO não é para quem busca milagre, para quem quer terceirizar decisões, para quem não está pronta para se confrontar, ou para quem prefere continuar vivendo só na cabeça."
              }
            }
          ]
        }
      ]
    };

    // Adicionar schema.org ao head
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schemaOrg);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const scrollToForm = () => {
    const formSection = document.getElementById('cta-section');
    formSection?.scrollIntoView({ behavior: 'smooth' });
  };

  const testimonials = [
    {
      text: "A Mentoria IMPACTO me devolveu algo que eu havia perdido: a capacidade de me governar. Não foi fácil, não foi rápido, mas foi real. Hoje eu não dependo mais de promessas externas. Eu construo minha própria realidade com solidez e clareza.",
      name: "Juliana Martins",
      role: "Empresária e Líder",
      image: "https://static.readdy.ai/image/5759e80cb249fc12074e871219d36a56/189cf568ccf1c6ae0a7018e89fc229be.jpeg"
    },
    {
      text: "Antes da IMPACTO, eu vivia em ciclos: começava projetos, me empolgava, e depois travava. Achava que era falta de disciplina. Mas era falta de estrutura interna. Hoje eu sei me conduzir, sei tomar decisões sem depender de validação externa. Isso mudou completamente meu negócio e minha vida.",
      name: "Camila Rodrigues",
      role: "Empreendedora Digital",
      image: "https://static.readdy.ai/image/5759e80cb249fc12074e871219d36a56/63b24ac5e4d02aeba33bce984c3ef4fe.jpeg"
    },
    {
      text: "O que mais me impactou foi perceber que eu não precisava de mais informação. Eu precisava de governo interno. A Larissa me ensinou a parar de terceirizar minhas decisões e assumir o comando da minha própria vida. Hoje eu não sigo gurus, eu me conduzo.",
      name: "Renata Silva",
      role: "Consultora e Mentora",
      image: "https://static.readdy.ai/image/5759e80cb249fc12074e871219d36a56/3c596a036705be1ea8d93da0396ec671.jpeg"
    },
    {
      text: "A IMPACTO não me vendeu sonhos. Me ensinou a construir realidade. Aprendi a manifestar com responsabilidade, a planejar considerando minha vida real, e principalmente, a sustentar aquilo que desejo sem me abandonar no processo. É transformador de verdade.",
      name: "Patrícia Almeida",
      role: "CEO e Investidora",
      image: "https://static.readdy.ai/image/5759e80cb249fc12074e871219d36a56/dc71f328f3a6e0f98f613a7d9d20ba4a.jpeg"
    },
    {
      text: "Eu estava cansada de promessas vazias e métodos milagrosos. A Mentoria IMPACTO foi diferente desde o primeiro dia: sem ilusões, sem atalhos, só verdade e estrutura. Hoje eu tenho clareza sobre quem sou e para onde vou. E isso não tem preço.",
      name: "Fernanda Costa",
      role: "Empresária e Palestrante",
      image: "https://static.readdy.ai/image/5759e80cb249fc12074e871219d36a56/7f8d7b169d62ccafbfe820375b0ccbc1.jpeg"
    }
  ];

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <>
      <div className="bg-black text-white">
        {/* Header/Navigation */}
        <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
          <nav className="container mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center">
              <h1 className="sr-only">Mentoria IMPACTO - Governo Interno Feminino</h1>
              <img 
                src="https://static.readdy.ai/image/5759e80cb249fc12074e871219d36a56/45ad6a47b9fb48ee035b554bf9c9a7f3.png" 
                alt="Mentoria IMPACTO" 
                className="h-12 w-auto"
              />
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#beneficios" className={`text-sm font-medium transition-colors cursor-pointer hover:text-[#D4AF37] ${scrolled ? 'text-gray-800' : 'text-white'}`}>Benefícios</a>
              <a href="#para-quem" className={`text-sm font-medium transition-colors cursor-pointer hover:text-[#D4AF37] ${scrolled ? 'text-gray-800' : 'text-white'}`}>Para Quem É</a>
              <a href="#depoimentos" className={`text-sm font-medium transition-colors cursor-pointer hover:text-[#D4AF37] ${scrolled ? 'text-gray-800' : 'text-white'}`}>Depoimentos</a>
              <button 
                onClick={scrollToForm}
                className="bg-[#D4AF37] text-black px-6 py-2.5 rounded-full font-semibold text-sm whitespace-nowrap hover:scale-105 transition-transform cursor-pointer"
              >
                Inscreva-se
              </button>
            </div>
          </nav>
        </header>

        {/* Hero Section */}
        <section className="relative h-screen flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src="https://public.readdy.ai/ai/img_res/e0586291c0e642bef74d926f94cff785.jpg"
              alt="Hero Background"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70"></div>
          </div>
          
          <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
            <h1 className="text-5xl md:text-7xl font-bold tracking-[4px] mb-6 text-white">
              MENTORIA IMPACTO
            </h1>
            <h2 className="text-3xl md:text-5xl font-light italic text-[#D4AF37] mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
              O Código do Governo Interno
            </h2>
            <p className="text-lg md:text-xl text-gray-200 opacity-90 max-w-2xl mx-auto mb-12">
              Para mulheres que cansaram de promessas e decidiram se governar
            </p>
            <button 
              onClick={scrollToForm}
              className="bg-white text-black px-12 py-4 rounded-full font-bold text-base hover:scale-105 transition-transform shadow-lg cursor-pointer whitespace-nowrap"
            >
              QUERO ME GOVERNAR
            </button>
          </div>
        </section>

        {/* Seção de Confronto */}
        <section className="bg-[#0A0A0A] py-20 md:py-32 px-6">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-3xl md:text-4xl mb-16 text-white/95" style={{ fontFamily: 'Playfair Display, serif' }}>
              Você já percebeu que existe algo errado quando:
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6 mb-20">
              <div className="bg-gradient-to-b from-[#1A1A1A] to-transparent border-l-4 border-[#D4AF37] p-8 rounded-lg">
                <p className="text-lg text-gray-300 leading-relaxed">
                  <span className="inline-block w-2 h-2 bg-[#D4AF37] mr-3 mb-1"></span>
                  todo mundo promete resultado rápido
                </p>
              </div>
              <div className="bg-gradient-to-b from-[#1A1A1A] to-transparent border-l-4 border-[#D4AF37] p-8 rounded-lg">
                <p className="text-lg text-gray-300 leading-relaxed">
                  <span className="inline-block w-2 h-2 bg-[#D4AF37] mr-3 mb-1"></span>
                  todo mundo tem um "método infalível"
                </p>
              </div>
              <div className="bg-gradient-to-b from-[#1A1A1A] to-transparent border-l-4 border-[#D4AF37] p-8 rounded-lg">
                <p className="text-lg text-gray-300 leading-relaxed">
                  <span className="inline-block w-2 h-2 bg-[#D4AF37] mr-3 mb-1"></span>
                  todo mundo diz que é só seguir o passo a passo
                </p>
              </div>
            </div>

            <div className="text-center max-w-4xl mx-auto">
              <p className="text-xl md:text-2xl italic text-[#D4AF37] mb-6">
                e, mesmo assim, você continua se sentindo perdida, sobrecarregada ou travada...
              </p>
              <p className="text-xl md:text-2xl italic text-[#D4AF37] mb-8">
                Não porque você não seja capaz.
              </p>
              <p className="text-2xl md:text-3xl font-semibold text-white">
                Mas porque o sistema não foi feito para te ensinar a se governar.
              </p>
            </div>

            <div className="mt-16 max-w-3xl mx-auto">
              <p className="text-xl text-gray-300 text-center mb-6">Ele foi feito para te manter:</p>
              <div className="space-y-4">
                {['dependente', 'insegura', 'sempre buscando fora', 'sempre achando que falta algo em você'].map((item, idx) => (
                  <div key={idx} className="bg-white/5 rounded-2xl p-6 backdrop-blur-sm">
                    <p className="text-lg text-gray-200 flex items-center">
                      <i className="ri-arrow-right-line text-[#D4AF37] text-2xl mr-4"></i>
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-20 text-center">
              <p className="text-2xl md:text-3xl font-bold text-white">
                Aqui, a conversa é outra.
              </p>
            </div>
          </div>
        </section>

        {/* A Verdade Que Ninguém Te Conta */}
        <section className="bg-gradient-to-br from-[#2C2C2C] to-black py-20 md:py-28 px-6 relative">
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-2/5 bg-[#D4AF37]"></div>
          
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-4xl md:text-5xl font-bold mb-12 text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
              A verdade que ninguém te conta
            </h2>
            
            <div className="space-y-6 mb-16">
              <p className="text-xl text-white/90 leading-relaxed">
                O problema não é falta de técnica.
              </p>
              <p className="text-xl text-white/90 leading-relaxed">
                Não é falta de espiritualidade.
              </p>
              <p className="text-xl text-white/90 leading-relaxed">
                Não é falta de força, talento ou sensibilidade.
              </p>
              <p className="text-2xl md:text-3xl font-bold text-[#D4AF37] leading-relaxed mt-8">
                O problema é falta de governo interno.
              </p>
            </div>

            <div className="bg-white/10 rounded-2xl p-8 mb-8">
              <h3 className="text-2xl font-semibold text-white mb-6">
                Quando você não se governa:
              </h3>
              <div className="space-y-4">
                {[
                  'o marketing decide por você',
                  'o medo decide por você',
                  'o sistema decide por você',
                  'o próximo "especialista" decide por você'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center">
                    <i className="ri-arrow-right-s-line text-[#D4AF37] text-3xl mr-4"></i>
                    <p className="text-lg text-gray-200">{item}</p>
                  </div>
                ))}
              </div>
            </div>

            <p className="text-xl text-gray-300 leading-relaxed italic">
              E uma mulher sem governo interno vira presa fácil de promessas exageradas, discursos bonitos e soluções que nunca se sustentam.
            </p>
          </div>
        </section>

        {/* Hero Secundário */}
        <section className="relative h-[70vh] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src="https://readdy.ai/api/search-image?query=abstract%20minimalist%20golden%20geometric%20shapes%20on%20pure%20black%20background%20elegant%20luxury%20design%20modern%20art%20sophisticated%20composition%20metallic%20gold%20elements%20clean%20aesthetic%20premium%20visual&width=1920&height=1080&seq=hero002&orientation=landscape"
              alt="Background"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-radial from-transparent via-black/60 to-black/80"></div>
          </div>
          
          <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
            <h2 className="text-4xl md:text-6xl font-bold mb-12 text-white leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              A Mentoria IMPACTO existe para romper esse ciclo
            </h2>
            
            <div className="grid md:grid-cols-3 gap-10 mt-16">
              <p className="text-lg italic text-gray-300">Não é mais um método</p>
              <p className="text-lg italic text-gray-300">Não é mais um curso</p>
              <p className="text-lg italic text-gray-300">Não é mais uma promessa de "agora vai"</p>
            </div>

            <div className="mt-16">
              <p className="text-2xl md:text-3xl font-bold text-[#D4AF37]">
                A Mentoria IMPACTO é um processo de construção do Governo Interno Feminino.
              </p>
            </div>
          </div>
        </section>

        {/* O Que Você NÃO Vai Encontrar */}
        <section className="bg-black py-20 md:py-28 px-6">
          <div className="container mx-auto max-w-5xl">
            <h2 className="text-3xl md:text-4xl mb-16 text-white leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              Antes de te explicar o que você vai ganhar,<br />
              preciso te dizer o que você NÃO vai encontrar aqui
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8 mb-20">
              {[
                'Promessas irreais',
                'Marketing extremista',
                'Linguagem infantil',
                'Espiritualidade desconectada da realidade',
                'Planejamento que ignora sua vida real',
                'Dependência emocional da mentora'
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-5">
                  <div className="w-12 h-12 rounded-full bg-[#8B0000] flex items-center justify-center flex-shrink-0">
                    <i className="ri-close-line text-white text-2xl"></i>
                  </div>
                  <p className="text-lg md:text-xl text-gray-400">{item}</p>
                </div>
              ))}
            </div>

            <div className="text-center max-w-4xl mx-auto">
              <p className="text-xl md:text-2xl italic text-[#D4AF37] leading-relaxed">
                Se você está procurando alguém para te convencer de que tudo é fácil, rápido e mágico —<br />
                a IMPACTO não é para você.
              </p>
            </div>
          </div>
        </section>

        {/* Detalhes da Mentoria */}
        <section className="bg-gradient-to-b from-black to-[#1A1A1A] py-20 md:py-32 px-6 relative">
          <div className="absolute inset-0 opacity-5">
            <div className="w-full h-full" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.05) 35px, rgba(255,255,255,.05) 70px)' }}></div>
          </div>
          
          <div className="container mx-auto max-w-5xl relative z-10">
            <h2 className="text-4xl md:text-5xl font-bold mb-14 text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
              O que é, de verdade, a Mentoria IMPACTO
            </h2>
            
            <p className="text-xl text-gray-300 mb-12 leading-relaxed">
              A Mentoria IMPACTO é um processo profundo e prático que integra:
            </p>

            <div className="space-y-5 mb-16">
              {[
                'identidade real',
                'planejamento pessoal',
                'blindagem da mentalidade',
                'mudança de comportamento',
                'decisão consciente',
                'execução possível',
                'manifestação com responsabilidade'
              ].map((item, idx) => (
                <div key={idx} className="bg-gradient-to-r from-transparent to-[#1A1A1A] border-l-4 border-[#D4AF37] rounded-r-xl p-7">
                  <p className="text-xl md:text-2xl font-medium text-white">{item}</p>
                </div>
              ))}
            </div>

            <div className="bg-[#D4AF37]/15 border-2 border-[#D4AF37] rounded-2xl p-10 text-center">
              <p className="text-3xl md:text-4xl font-bold text-[#D4AF37]" style={{ fontFamily: 'Playfair Display, serif' }}>
                O Código do Governo Interno
              </p>
            </div>

            <div className="mt-16 text-center max-w-3xl mx-auto">
              <p className="text-2xl md:text-3xl font-semibold text-white mb-8">
                Manifestar não é pedir.
              </p>
              <p className="text-xl md:text-2xl text-[#D4AF37] italic">
                Manifestar é se tornar capaz de sustentar aquilo que se deseja viver.
              </p>
            </div>

            <div className="mt-20 bg-[#1A1A1A] rounded-2xl p-10">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-8">
                Aqui você aprende, inclusive, a fugir do marketing que domina decisões
              </h3>
              <p className="text-lg text-gray-300 mb-6">Parte do governo interno é aprender a:</p>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  'não cair em promessas vazias',
                  'não decidir por impulso',
                  'não terceirizar sua autoridade',
                  'não confundir desejo com fuga',
                  'não se sabotar acreditando em atalhos'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <i className="ri-shield-check-line text-[#D4AF37] text-xl mt-1"></i>
                    <p className="text-gray-200">{item}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-16 text-center">
              <p className="text-2xl md:text-3xl font-bold text-white">
                A IMPACTO te devolve algo que o sistema tenta tirar o tempo todo:
              </p>
              <p className="text-4xl md:text-5xl font-bold text-[#D4AF37] mt-6">
                lucidez.
              </p>
            </div>
          </div>
        </section>

        {/* Para Quem É / Não É */}
        <section id="para-quem" className="bg-black py-20 md:py-28 px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="grid md:grid-cols-2 gap-16">
              {/* Para Quem É */}
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-[#D4AF37] mb-10">
                  Para quem é a Mentoria IMPACTO
                </h2>
                <div className="space-y-6">
                  {[
                    'Mulheres empreendedoras, empresárias ou líderes',
                    'Mulheres inteligentes, sensíveis e profundas',
                    'Mulheres cansadas de girar em ciclos',
                    'Mulheres que querem prosperar sem se perder',
                    'Mulheres prontas para assumir responsabilidade pela própria vida'
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-start gap-4">
                      <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                        <i className="ri-checkbox-circle-fill text-[#D4AF37] text-3xl"></i>
                      </div>
                      <p className="text-lg md:text-xl text-white leading-relaxed">{item}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Para Quem NÃO É */}
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-[#A52A2A] mb-10">
                  Para quem NÃO é
                </h2>
                <div className="space-y-6">
                  {[
                    'Para quem busca milagre',
                    'Para quem quer terceirizar decisões',
                    'Para quem não está pronta para se confrontar',
                    'Para quem prefere continuar vivendo só na cabeça'
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-start gap-4">
                      <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                        <i className="ri-close-circle-fill text-[#A52A2A] text-3xl"></i>
                      </div>
                      <p className="text-lg md:text-xl text-gray-400 leading-relaxed">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefícios */}
        <section id="beneficios" className="bg-gradient-radial from-[#1A1A1A] via-black to-black py-20 md:py-32 px-6">
          <div className="container mx-auto max-w-7xl">
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-20 text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
              O que você conquista ao passar pela IMPACTO
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: 'ri-lightbulb-line',
                  title: 'Clareza profunda',
                  desc: 'sobre quem você é e o que deseja de verdade'
                },
                {
                  icon: 'ri-calendar-check-line',
                  title: 'Organização intencional',
                  desc: 'dos próximos anos da sua vida'
                },
                {
                  icon: 'ri-eye-off-line',
                  title: 'Fim das ilusões internas',
                  desc: 'e dos sonhos que nunca saem do papel'
                },
                {
                  icon: 'ri-compass-3-line',
                  title: 'Mais decisão',
                  desc: 'e menos oscilação emocional'
                },
                {
                  icon: 'ri-rocket-line',
                  title: 'Execução possível',
                  desc: 'com o que você já tem'
                },
                {
                  icon: 'ri-shield-star-line',
                  title: 'Posicionamento firme',
                  desc: 'e respeitado'
                },
                {
                  icon: 'ri-plant-line',
                  title: 'Autonomia completa',
                  desc: 'emocional, financeira e comportamental'
                },
                {
                  icon: 'ri-anchor-line',
                  title: 'Solidez interna',
                  desc: 'e direção clara'
                },
                {
                  icon: 'ri-trophy-line',
                  title: 'Capacidade de sustentar',
                  desc: 'aquilo que você manifesta'
                }
              ].map((benefit, idx) => (
                <div 
                  key={idx} 
                  className="bg-gradient-to-b from-[#1F1F1F] to-transparent border-t-4 border-[#D4AF37] rounded-xl p-8 hover:shadow-2xl hover:shadow-[#D4AF37]/20 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center mb-6">
                    <i className={`${benefit.icon} text-[#D4AF37] text-5xl`}></i>
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-4">{benefit.title}</h3>
                  <p className="text-lg text-gray-300 leading-relaxed">{benefit.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Por Que Confiar */}
        <section className="bg-black py-20 md:py-24 px-6">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl md:text-4xl mb-14 text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
              Por que você pode confiar nesse processo
            </h2>
            
            <div className="grid md:grid-cols-3 gap-12 mb-16">
              {[
                { icon: 'ri-building-line', text: 'Constrói Estrutura' },
                { icon: 'ri-hand-heart-line', text: 'Fortalece Autonomia' },
                { icon: 'ri-flashlight-line', text: 'Devolve Poder Interno' }
              ].map((item, idx) => (
                <div key={idx} className="flex flex-col items-center">
                  <div className="w-16 h-16 flex items-center justify-center mb-6">
                    <i className={`${item.icon} text-[#D4AF37] text-6xl`}></i>
                  </div>
                  <p className="text-xl md:text-2xl font-medium text-[#D4AF37]">{item.text}</p>
                </div>
              ))}
            </div>

            <p className="text-2xl md:text-3xl italic text-white/90 leading-relaxed max-w-3xl mx-auto" style={{ fontFamily: 'Playfair Display, serif' }}>
              Aqui você não aprende a "seguir alguém".<br />
              Você aprende a se conduzir.
            </p>
          </div>
        </section>

        {/* Quem Vai Te Guiar - NOVA SEÇÃO */}
        <section className="bg-gradient-to-b from-black via-[#1A1A1A] to-black py-20 md:py-32 px-6">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-20 text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
              Quem Vai Te Guiar
            </h2>
            
            <div className="grid md:grid-cols-2 gap-16 items-center">
              {/* Imagem */}
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#D4AF37]/20 to-transparent rounded-3xl blur-2xl"></div>
                <div className="relative">
                  <img 
                    src="https://static.readdy.ai/image/5759e80cb249fc12074e871219d36a56/af7228439990b621ad207692bf50020e.jpeg"
                    alt="Larissa - Mentora IMPACTO"
                    className="w-full h-auto rounded-2xl object-cover object-top shadow-2xl"
                  />
                  <div className="absolute -bottom-6 -right-6 bg-[#D4AF37] rounded-2xl px-8 py-4">
                    <p className="text-black font-bold text-xl">+20 anos</p>
                    <p className="text-black text-sm">de experiência</p>
                  </div>
                </div>
              </div>

              {/* Conteúdo */}
              <div className="space-y-6">
                <h3 className="text-3xl md:text-4xl font-bold text-[#D4AF37] mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Prazer, eu sou Larissa.
                </h3>
                
                <p className="text-lg md:text-xl text-gray-300 leading-relaxed">
                  Há mais de 20 anos, eu transito entre estratégia, comportamento humano e vida real.
                </p>
                
                <p className="text-lg md:text-xl text-gray-300 leading-relaxed">
                  Já acompanhei de perto o brilho das construções e o silêncio dos recomeços — aqueles que exigem mais caráter do que discurso.
                </p>
                
                <p className="text-lg md:text-xl text-gray-300 leading-relaxed">
                  Durante muito tempo, segui métodos, promessas e sistemas que diziam exatamente o que fazer, mas não ensinavam a se sustentar por dentro.
                </p>
                
                <div className="bg-white/5 border-l-4 border-[#D4AF37] rounded-r-xl p-6 my-8">
                  <p className="text-xl md:text-2xl text-white font-medium italic" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Quando parei de buscar soluções externas e passei a organizar minha vida internamente, tudo mudou.
                  </p>
                </div>
                
                <p className="text-lg text-gray-400 italic">
                  Não por mágica.<br />
                  Por decisão, estrutura e responsabilidade.
                </p>
                
                <div className="pt-6">
                  <p className="text-xl md:text-2xl text-white leading-relaxed font-medium">
                    Hoje, eu ajudo mulheres a se governarem, para que aquilo que desejam deixe de ser apenas intenção e se torne vida possível.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Depoimentos */}
        <section id="depoimentos" className="bg-[#2A2A2A] py-20 md:py-28 px-6">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-4xl md:text-5xl text-center mb-20 text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
              O Que Mulheres Reais Dizem
            </h2>
            
            <div className="relative bg-[#1A1A1A] rounded-2xl p-10 md:p-12">
              <div className="absolute top-8 left-8 bg-black rounded-3xl px-6 py-3 flex items-center gap-2">
                <i className="ri-star-fill text-[#D4AF37] text-xl"></i>
                <span className="text-white font-semibold text-lg">5.0</span>
              </div>
              
              <div className="mt-16">
                <i className="ri-double-quotes-l text-[#D4AF37] text-6xl opacity-50"></i>
                <p className="text-xl md:text-2xl text-white/95 leading-relaxed mt-6 mb-12 max-w-3xl" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {testimonials[currentTestimonial].text}
                </p>
                
                <div className="flex items-center gap-5">
                  <img 
                    src={testimonials[currentTestimonial].image}
                    alt={testimonials[currentTestimonial].name}
                    className="w-20 h-20 rounded-full object-cover object-top"
                  />
                  <div>
                    <p className="text-xl font-bold text-white">{testimonials[currentTestimonial].name}</p>
                    <p className="text-base text-gray-400">{testimonials[currentTestimonial].role}</p>
                  </div>
                </div>
              </div>

              <div className="absolute bottom-8 right-8 flex gap-3">
                <button 
                  onClick={prevTestimonial}
                  className="w-14 h-14 rounded-lg bg-white border border-gray-300 flex items-center justify-center hover:rotate-[-2deg] transition-transform cursor-pointer"
                >
                  <i className="ri-arrow-left-line text-black text-xl"></i>
                </button>
                <button 
                  onClick={nextTestimonial}
                  className="w-14 h-14 rounded-lg bg-black flex items-center justify-center hover:rotate-2 transition-transform cursor-pointer"
                >
                  <i className="ri-arrow-right-line text-white text-xl"></i>
                </button>
              </div>

              {/* Indicadores de navegação */}
              <div className="flex justify-center gap-2 mt-8">
                {testimonials.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentTestimonial(idx)}
                    className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                      idx === currentTestimonial 
                        ? 'bg-[#D4AF37] w-8' 
                        : 'bg-gray-600 hover:bg-gray-500'
                    }`}
                    aria-label={`Ver depoimento ${idx + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Principal */}
        <section id="cta-section" className="relative h-[80vh] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src="https://readdy.ai/api/search-image?query=artistic%20black%20and%20white%20photography%20woman%20in%20moment%20of%20decision%20dramatic%20cinematic%20lighting%20contemplative%20powerful%20feminine%20strength%20elegant%20composition%20high%20contrast%20emotional%20depth&width=1920&height=1080&seq=cta001&orientation=landscape"
              alt="CTA Background"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-transparent to-black/70"></div>
          </div>
          
          <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl text-gray-300 mb-8">
              Você pode continuar:
            </h2>
            
            <div className="space-y-3 mb-12 text-lg md:text-xl italic text-gray-400">
              <p className="pl-8">consumindo conteúdos</p>
              <p className="pl-8">comprando promessas</p>
              <p className="pl-8">sentindo que entende tudo, mas não avança</p>
            </div>

            <div className="w-48 h-px bg-[#D4AF37] mx-auto my-12"></div>

            <p className="text-2xl md:text-3xl font-bold text-[#D4AF37] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Ou pode decidir assumir o comando da sua vida.
            </p>

            <p className="text-3xl md:text-4xl font-bold text-white mb-16">
              Quem não se governa, é governada.
            </p>

            <a 
              href="https://forms.gle/M5HGeQ7kXMsa2ekJ9"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-[#D4AF37] text-black px-12 md:px-16 py-5 rounded-full font-bold text-lg md:text-xl hover:scale-110 transition-all shadow-2xl shadow-[#D4AF37]/40 cursor-pointer whitespace-nowrap"
            >
              ENTRAR NA MENTORIA IMPACTO
            </a>
          </div>
        </section>

        {/* Última Verdade */}
        <section className="bg-black py-20 md:py-28 px-6">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl md:text-4xl mb-12 text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
              Última verdade (e talvez a mais importante)
            </h2>
            
            <p className="text-2xl md:text-3xl font-medium text-white leading-relaxed mb-10 max-w-3xl mx-auto">
              A IMPACTO não promete uma vida perfeita.<br />
              Ela entrega capacidade interna para governar a vida real.
            </p>

            <p className="text-4xl md:text-5xl font-bold italic text-[#D4AF37] mb-16" style={{ fontFamily: 'Playfair Display, serif' }}>
              E isso muda tudo.
            </p>

            <a 
              href="https://forms.gle/M5HGeQ7kXMsa2ekJ9"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-[#D4AF37] text-black px-12 md:px-16 py-5 rounded-full font-bold text-lg md:text-xl hover:scale-110 transition-all shadow-2xl shadow-[#D4AF37]/40 cursor-pointer whitespace-nowrap"
            >
              ENTRAR NA MENTORIA IMPACTO
            </a>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gradient-to-b from-[#0F0F0F] to-black rounded-t-[32px] pt-20 pb-10 px-6 relative overflow-hidden">
          <div className="absolute bottom-20 left-1/2 -translate-x-1/2 text-[200px] font-black text-white/3 whitespace-nowrap pointer-events-none">
            IMPACTO
          </div>
          
          <div className="container mx-auto max-w-7xl relative z-10">
            <div className="grid md:grid-cols-4 gap-12 mb-16">
              {/* Branding */}
              <div className="md:col-span-1">
                <h3 className="text-5xl font-bold text-[#D4AF37] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  IMPACTO
                </h3>
                <p className="text-base text-gray-400">Governo Interno Feminino</p>
              </div>

              {/* Links */}
              <div>
                <h4 className="text-lg font-medium text-white mb-6">Navegação</h4>
                <ul className="space-y-4">
                  {[
                    { text: 'Sobre a Mentoria', href: '#detalhes' },
                    { text: 'Para Quem É', href: '#para-quem' },
                    { text: 'Benefícios', href: '#beneficios' },
                    { text: 'Depoimentos', href: '#depoimentos' },
                    { text: 'Inscrição', href: '#cta-section' }
                  ].map((link, idx) => (
                    <li key={idx}>
                      <a 
                        href={link.href} 
                        className="text-base text-gray-400 hover:text-[#D4AF37] hover:translate-x-1 transition-all inline-block cursor-pointer"
                      >
                        {link.text}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Social */}
              <div>
                <h4 className="text-lg font-medium text-white mb-6">Conecte-se</h4>
                <div className="space-y-5">
                  <a 
                    href="https://www.instagram.com/larimoraesreal/" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-gray-400 hover:text-[#D4AF37] transition-colors group cursor-pointer"
                  >
                    <i className="ri-instagram-line text-2xl text-[#D4AF37] group-hover:scale-125 transition-transform"></i>
                    <span className="text-base">Instagram</span>
                  </a>
                  <a 
                    href="#" 
                    className="flex items-center gap-3 text-gray-400 hover:text-[#D4AF37] transition-colors group cursor-pointer"
                  >
                    <i className="ri-youtube-line text-2xl text-[#D4AF37] group-hover:scale-125 transition-transform"></i>
                    <span className="text-base">YouTube</span>
                  </a>
                  <a 
                    href="#" 
                    className="flex items-center gap-3 text-gray-400 hover:text-[#D4AF37] transition-colors group cursor-pointer"
                  >
                    <i className="ri-facebook-circle-line text-2xl text-[#D4AF37] group-hover:scale-125 transition-transform"></i>
                    <span className="text-base">Facebook</span>
                  </a>
                </div>
              </div>

              {/* Newsletter */}
              <div>
                <h4 className="text-lg font-medium text-white mb-3">Receba Conteúdos Exclusivos</h4>
                <p className="text-sm text-gray-400 mb-6">Insights sobre governo interno direto no seu email</p>
                <form 
                  id="newsletter-form"
                  data-readdy-form
                  action="https://readdy.ai/api/form/d51g7nt1e0me1634tme0"
                  method="POST"
                  className="relative"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const form = e.currentTarget;
                    const emailInput = form.querySelector('input[name="email"]') as HTMLInputElement;
                    
                    if (!emailInput.value) {
                      alert('Por favor, insira seu email');
                      return;
                    }

                    const formData = new URLSearchParams();
                    formData.append('email', emailInput.value);

                    fetch('https://readdy.ai/api/form/d51g7nt1e0me1634tme0', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                      },
                      body: formData.toString()
                    })
                    .then(response => {
                      if (response.ok) {
                        alert('Inscrição realizada com sucesso!');
                        emailInput.value = '';
                      } else {
                        alert('Erro ao realizar inscrição. Tente novamente.');
                      }
                    })
                    .catch(() => {
                      alert('Erro ao realizar inscrição. Tente novamente.');
                    });
                  }}
                >
                  <input 
                    type="email"
                    name="email"
                    placeholder="Seu melhor email"
                    required
                    className="w-full bg-white/10 border border-gray-700 rounded-l-3xl rounded-r-3xl px-6 py-4 pr-16 text-white placeholder-gray-500 focus:outline-none focus:border-[#D4AF37] transition-colors"
                  />
                  <button 
                    type="submit"
                    className="absolute right-1 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-[#D4AF37] flex items-center justify-center hover:rotate-90 transition-transform cursor-pointer"
                  >
                    <i className="ri-arrow-right-line text-black text-xl"></i>
                  </button>
                </form>
              </div>
            </div>

            <div className="border-t border-[#1A1A1A] pt-8">
              <p className="text-center text-sm text-gray-500">
                © 2025 Mentoria IMPACTO. Todos os direitos reservados. | <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="hover:text-[#D4AF37] transition-colors cursor-pointer">Powered by Readdy</a>
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
